import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import HttpService from '@/services/http';

import { WhiteLabelCustomDomainStatus, MODEL_NAME, ROUTES } from './constants';
import {
  buildBrandingPayload,
  getHttpStatus,
  isEmptyLocalResponse,
  mergeDomainResponse,
  unwrapEntity,
} from './utils';

const versionConflictNotification = () => ({
  visible: true,
  type: 'warning',
  message: getIntl().formatMessage({ id: 'whiteLabel.errors.versionConflict' }),
});

const api = bindServices(MODEL_NAME);
const namespace = MODEL_NAME;

/** Per-resource cache. UI spinners use `state.loading.effects[\`${namespace}/fetchLocal\`]`, etc. */
const createResourceState = () => ({
  error: null,
  loaded: false,
});

const initialState = {
  canEdit: false,
  isCreate: true,
  local: {
    ...createResourceState(),
    data: null,
  },
  effective: {
    ...createResourceState(),
    data: null,
  },
  brandingSaving: false,
  domainBusy: false,
  /** One-shot DNS token from register response (never returned again by API). */
  domainVerification: null,
  versionConflict: false,
};

const WhiteLabelModel = {
  namespace,
  state: initialState,
  effects: {
    *init({ payload }, { put }) {
      yield put({ type: 'setCanEdit', payload: payload?.canEdit ?? false });
      yield put({ type: 'fetchLocal', payload: { force: true } });
      yield put({ type: 'fetchEffective', payload: { force: true } });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *fetchLocal({ payload }, { call, put, select }) {
      const { loaded } = yield select((state) => state[namespace].local);
      const inFlight = yield select(
        (state) => state.loading.effects[`${namespace}/fetchLocal`],
      );
      if (!payload?.force && (loaded || inFlight)) return;

      yield put({ type: 'setLocalState', payload: { error: null } });
      try {
        const result = yield call(
          api.getAll,
          {
            route: ROUTES.LOCAL,
            readPriority: true,
          },
          { suppressErrorNotification: true },
        );

        // umi-request treats 204 as success with empty body (`''`)
        if (isEmptyLocalResponse(result)) {
          yield put({
            type: 'updateState',
            payload: {
              isCreate: true,
              domainVerification: null,
              versionConflict: false,
            },
          });
          yield put({
            type: 'setLocalState',
            payload: { loaded: true, data: null, error: null },
          });
          return;
        }

        const data = unwrapEntity(result);
        yield put({
          type: 'updateState',
          payload: {
            isCreate: false,
            versionConflict: false,
          },
        });
        yield put({
          type: 'setLocalState',
          payload: { loaded: true, data, error: null },
        });
      } catch (error) {
        const status = getHttpStatus(error);
        if (status === 204) {
          yield put({
            type: 'updateState',
            payload: { isCreate: true, domainVerification: null },
          });
          yield put({
            type: 'setLocalState',
            payload: { loaded: true, data: null, error: null },
          });
          return;
        }
        yield put({
          type: 'setLocalState',
          payload: {
            loaded: false,
            error: error?.message || 'FETCH_ERROR',
          },
        });
      }
    },

    *fetchEffective({ payload }, { call, put, select }) {
      const { loaded } = yield select((state) => state[namespace].effective);
      const inFlight = yield select(
        (state) => state.loading.effects[`${namespace}/fetchEffective`],
      );
      if (!payload?.force && (loaded || inFlight)) return;

      yield put({ type: 'setEffectiveState', payload: { error: null } });
      try {
        const response = yield call(
          api.getAll,
          {
            route: ROUTES.EFFECTIVE,
            readPriority: true,
          },
          { suppressErrorNotification: true },
        );
        const data = unwrapEntity(response);
        yield put({
          type: 'setEffectiveState',
          payload: { loaded: true, data, error: null },
        });
      } catch (error) {
        yield put({
          type: 'setEffectiveState',
          payload: {
            loaded: false,
            error: error?.message || 'FETCH_ERROR',
          },
        });
      }
    },

    *saveBranding({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const isCreate = yield select((state) => state[namespace].isCreate);
      const local = yield select((state) => state[namespace].local.data);
      const version = local?.version;

      yield put({ type: 'updateState', payload: { brandingSaving: true, versionConflict: false } });
      try {
        const body = buildBrandingPayload(payload, { version, isCreate });
        const response = yield call(api.update, {
          route: ROUTES.LOCAL,
          data: body,
        });
        const data = unwrapEntity(response) ?? { ...local, ...body };

        yield put({
          type: 'updateState',
          payload: { isCreate: false, brandingSaving: false, versionConflict: false },
        });
        yield put({
          type: 'setLocalState',
          payload: { data, loaded: true, error: null },
        });
        yield put({ type: 'fetchEffective', payload: { force: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        const status = getHttpStatus(error);
        yield put({ type: 'updateState', payload: { brandingSaving: false } });

        if (status === 409) {
          yield put({ type: 'updateState', payload: { versionConflict: true } });
          yield put({ type: 'fetchLocal', payload: { force: true } });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: versionConflictNotification(),
          });
          return;
        }

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error', message: error?.message },
        });
      }
    },

    *registerDomain({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(api.create, {
          route: ROUTES.CUSTOM_DOMAIN,
          data: {
            customDomain: payload.customDomain,
            version: local?.version,
          },
        });
        const data = unwrapEntity(response);
        yield put({
          type: 'updateState',
          payload: {
            domainBusy: false,
            isCreate: false,
            domainVerification: {
              verificationToken: data?.verificationToken,
              txtRecordName: data?.txtRecordName,
              txtRecordValue: data?.txtRecordValue ?? data?.verificationToken,
              customDomain: data?.customDomain,
            },
          },
        });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *verifyDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(api.create, {
          route: ROUTES.CUSTOM_DOMAIN_VERIFY,
          data: { version: local?.version },
        });
        const data = unwrapEntity(response);
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *activateDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(api.create, {
          route: ROUTES.CUSTOM_DOMAIN_ACTIVATE,
          data: { version: local?.version },
        });
        const data = unwrapEntity(response);
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *disableDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(api.create, {
          route: ROUTES.CUSTOM_DOMAIN_DISABLE,
          data: { version: local?.version },
        });
        const data = unwrapEntity(response);
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *clearDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(
          [HttpService.getInstance(), 'request'],
          `/${ROUTES.CUSTOM_DOMAIN}`,
          {
            method: 'delete',
            data: { version: local?.version },
            headers: { 'Content-Type': 'application/json' },
            namespace,
          },
        );
        const data = unwrapEntity(response);
        yield put({
          type: 'updateState',
          payload: { domainBusy: false, domainVerification: null },
        });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, {
              ...(data || {}),
              customDomain: null,
              customDomainStatus: WhiteLabelCustomDomainStatus.NotConfigured,
              verificationToken: null,
              txtRecordName: null,
              txtRecordValue: null,
            }),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },
  },
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    setCanEdit(state, { payload }) {
      return { ...state, canEdit: !!payload };
    },
    setLocalState(state, { payload }) {
      return { ...state, local: { ...state.local, ...payload } };
    },
    setEffectiveState(state, { payload }) {
      return { ...state, effective: { ...state.effective, ...payload } };
    },
    reset() {
      return { ...initialState };
    },
  },
};

function* handleDomainError(error, put) {
  const status = getHttpStatus(error);
  if (status === 409) {
    yield put({ type: 'updateState', payload: { versionConflict: true } });
    yield put({ type: `${namespace}/fetchLocal`, payload: { force: true } });
    yield put({
      type: 'global/showNotificationsGlobal',
      payload: versionConflictNotification(),
    });
    return;
  }
  yield put({
    type: 'global/showNotificationsGlobal',
    payload: { visible: true, type: 'error', message: error?.message },
  });
}

export default WhiteLabelModel;
